import {
  calibrationFromRect,
  isFinitePositive,
  templateFrameStatus,
  templateRect,
  type CarryOverAnchor,
  type FrameCalibrationStatus,
  type PrintAreaMm,
  type SizeTier,
} from '@designdeck/shared';
import type { AdminProductPosition } from '@/api/admin';
import type { AdminMockupTemplate as ProductMockupTemplate } from '@/api/admin';
import type { EditableArea } from './PrintAreaEditor';

/**
 * A position's design-area values while they are being edited. The frame
 * (x, y, width, height) is in the active mockup's native pixel space and
 * belongs to that template's calibration; the rest belongs to the
 * position's print area. Shared by the per-product admin page and the
 * frame-review walkthrough so both derive the editor rect the same way.
 */
export interface PrintAreaDraft extends EditableArea {
  rotation: number;
  maxWidthMm: number | null;
  maxHeightMm: number | null;
  /**
   * Admin marker RUNDUM; null = derived tier applies. A KLEIN value only
   * shows up while migration retire_klein_override is pending and is
   * cleared on save.
   */
  sizeTierOverride: SizeTier | null;
  /** Area exceptions to the product carry-over; null inherits. */
  carryOverPercent: number | null;
  carryOverAnchor: CarryOverAnchor | null;
  carryOverTopOffsetMm: number | null;
}

/**
 * Where the frame a draft starts with comes from:
 * - `calibration`: the template is calibrated, the frame is its rectangle.
 * - `draft`: the template waits for review (NEEDS_REVIEW) and the frame is
 *   its stored draft (R29).
 * - `suggestion`: nothing usable is stored; the frame is a start
 *   suggestion that only counts once the admin saves it.
 */
export type FrameSource = 'calibration' | 'draft' | 'suggestion';

/** Share of the image width the start suggestion covers. */
export const SUGGESTION_WIDTH_SHARE = 0.4;

const MIN_SUGGESTION_PX = 10;

/** Carry-over exceptions of a print area; null inherits the product value. */
function carryOverOf(
  printArea:
    | {
        carryOverPercent?: number | null;
        carryOverAnchor?: CarryOverAnchor | null;
        carryOverTopOffsetMm?: number | null;
      }
    | null
    | undefined
) {
  return {
    carryOverPercent: printArea?.carryOverPercent ?? null,
    carryOverAnchor: printArea?.carryOverAnchor ?? null,
    carryOverTopOffsetMm: printArea?.carryOverTopOffsetMm ?? null,
  };
}

/** Both millimetre values of a print area or draft, when they are set. */
export function printAreaMmOf(
  area:
    | { maxWidthMm?: number | null; maxHeightMm?: number | null }
    | null
    | undefined
): PrintAreaMm | null {
  return area &&
    isFinitePositive(area.maxWidthMm) &&
    isFinitePositive(area.maxHeightMm)
    ? { maxWidthMm: area.maxWidthMm, maxHeightMm: area.maxHeightMm }
    : null;
}

/**
 * The millimetre field a calibration lacks (R9), or null when both are
 * set. Saving a frame is blocked while one is missing.
 */
export function missingMmField(
  area:
    | { maxWidthMm?: number | null; maxHeightMm?: number | null }
    | null
    | undefined
): keyof PrintAreaMm | null {
  if (!isFinitePositive(area?.maxWidthMm)) return 'maxWidthMm';
  if (!isFinitePositive(area?.maxHeightMm)) return 'maxHeightMm';
  return null;
}

/** German label of a millimetre field, for the blocked-save hint. */
export const MM_FIELD_LABEL: Record<keyof PrintAreaMm, string> = {
  maxWidthMm: 'Druckbreite (maxWidthMm)',
  maxHeightMm: 'Druckhöhe (maxHeightMm)',
};

/** The stored calibration status of a template on its position. */
export function frameStatusOf(
  position: AdminProductPosition | undefined,
  template: ProductMockupTemplate | undefined
): FrameCalibrationStatus {
  if (!template) return 'uncalibrated';
  return templateFrameStatus(position?.printArea, template);
}

/** Where the frame of a template's draft comes from (see FrameSource). */
export function frameSourceOf(
  position: AdminProductPosition | undefined,
  template: ProductMockupTemplate | undefined
): FrameSource {
  const status = frameStatusOf(position, template);
  if (status === 'calibrated') return 'calibration';
  if (status === 'inconsistent' && isFinitePositive(template?.pxPerMm)) {
    return 'draft';
  }
  return 'suggestion';
}

/**
 * The frame a drawn rectangle stands for: the width and the position are
 * the admin's, the height follows from the millimetres (R8). It is the
 * same round trip the server makes, `templateRect(calibrationFromRect())`,
 * so the preview is what a save stores. Without both millimetre values
 * there is nothing to follow and the rectangle is kept as drawn.
 */
export function frameFromDrawn(
  area: { maxWidthMm?: number | null; maxHeightMm?: number | null },
  rect: EditableArea,
  mockupWidth: number
): EditableArea {
  const mm = printAreaMmOf(area);
  if (!mm) return rect;
  const result = calibrationFromRect(mm, rect, mockupWidth);
  if (!result.ok) return rect;
  return templateRect(mm, result.calibration);
}

/**
 * Build the editable draft for a position on a given mockup template.
 *
 * Every template, default or not, carries its own calibration: a
 * calibrated template and a template to review show their stored frame
 * (`templateRect` of the millimetres and the calibration); any other gets
 * a start suggestion, 40 % of the image width and centred, whose height
 * follows the millimetres when they are known.
 */
export function buildDraft(
  position: AdminProductPosition | undefined,
  template: ProductMockupTemplate | undefined,
  mockupWidth: number,
  mockupHeight: number
): PrintAreaDraft {
  const printArea = position?.printArea;
  const fields = {
    rotation: printArea?.rotation ?? 0,
    maxWidthMm: printArea?.maxWidthMm ?? null,
    maxHeightMm: printArea?.maxHeightMm ?? null,
    sizeTierOverride: printArea?.sizeTierOverride ?? null,
    ...carryOverOf(printArea),
  };

  const mm = printAreaMmOf(printArea);
  if (mm && template && frameSourceOf(position, template) !== 'suggestion') {
    return {
      ...templateRect(mm, {
        pxPerMm: template.pxPerMm ?? 0,
        offsetXCenterPx: template.offsetXCenterPx ?? 0,
        offsetYTopPx: template.offsetYTopPx ?? 0,
        mockupWidthPx: mockupWidth,
      }),
      ...fields,
    };
  }

  return { ...suggestedFrame(mm, mockupWidth, mockupHeight), ...fields };
}

/** The start suggestion: 40 % of the image width, centred. */
function suggestedFrame(
  mm: PrintAreaMm | null,
  mockupWidth: number,
  mockupHeight: number
): EditableArea {
  const width = Math.max(
    MIN_SUGGESTION_PX,
    Math.round(mockupWidth * SUGGESTION_WIDTH_SHARE)
  );
  const height = mm
    ? Math.max(1, Math.round((width * mm.maxHeightMm) / mm.maxWidthMm))
    : Math.max(
        MIN_SUGGESTION_PX,
        Math.round(mockupHeight * SUGGESTION_WIDTH_SHARE)
      );
  return {
    x: Math.round((mockupWidth - width) / 2),
    y: Math.round((mockupHeight - height) / 2),
    width,
    height,
  };
}

/** Whether two drafts draw the same frame. */
export function framesEqual(a: EditableArea, b: EditableArea): boolean {
  return (
    a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height
  );
}

/** Whether two drafts carry the same print-area (position) values. */
export function printAreaFieldsEqual(
  a: PrintAreaDraft,
  b: PrintAreaDraft
): boolean {
  return (
    a.rotation === b.rotation &&
    a.maxWidthMm === b.maxWidthMm &&
    a.maxHeightMm === b.maxHeightMm &&
    a.sizeTierOverride === b.sizeTierOverride &&
    a.carryOverPercent === b.carryOverPercent &&
    a.carryOverAnchor === b.carryOverAnchor &&
    a.carryOverTopOffsetMm === b.carryOverTopOffsetMm
  );
}

/**
 * Field-by-field equality of two drafts. Shared by the per-product admin page
 * and the frame-review walkthrough to drive their "unsaved changes" flag, so a
 * new draft field only needs updating in one place.
 */
export function draftsEqual(a: PrintAreaDraft, b: PrintAreaDraft): boolean {
  return framesEqual(a, b) && printAreaFieldsEqual(a, b);
}
